import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardBody, Col, Form, Input, Label, Row, Spinner, FormGroup } from 'reactstrap';
import { toast } from 'react-toastify';
import { get, postJson, putJson } from '../../../hook/request';
import { Btn, H3 } from '../../../AbstractElements';
import Loader from '../../../Components/Widgets/Loader/generic';

type FormState = {
  nome: string;
  descrizione: string;
  abilitato: 1 | 0;
};

export default function PermissionTypeForm() {
  const { slug } = useParams<{ slug: string }>();
  const isEdit = useMemo(() => !!slug && slug !== 'nuovo', [slug]);

  const [loading, setLoading] = useState<boolean>(isEdit);

  const [form, setForm] = useState<FormState>({
    nome: '',
    descrizione: '',
    abilitato: 1,
  });

  // GET dettaglio in edit: permission/type?ID=slug
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!isEdit) return;

      try {
        setLoading(true);

        const res = await get<unknown>(`permissions/type?ID=${encodeURIComponent(String(slug))}`);
        const raw = (res as any)?.data ?? res;
        const data = Array.isArray(raw) ? raw[0] : raw;

        if (!mounted) return;

        setForm({
          nome: data?.nome ?? '',
          descrizione: data?.descrizione ?? '',
          abilitato: data?.abilitato === 1 || data?.abilitato === '1' ? 1 : 0,
        });
      } catch (err: any) {
        const msg = err?.response?.data?.message ?? err?.message ?? 'Errore nel caricamento';
        toast.error(msg);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [isEdit, slug]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const user_email = localStorage.getItem('email');
    const payload = {
      ID: isEdit ? slug : undefined,
      nome: form.nome,
      descrizione: form.descrizione,
      abilitato: form.abilitato,
      user_email,
    };

    try {
      setLoading(true);

      if (isEdit) {
        await putJson('permissions/type', payload);
        toast.success('Tipo permesso aggiornato!');
      } else {
        const payloadCreate = {
          nome: form.nome,
          descrizione: form.descrizione,
          abilitato: form.abilitato,
          user_email,
        };

        await postJson('permissions/type', payloadCreate);
        toast.success('Tipo permesso creato!');

        setForm({ nome: '', descrizione: '', abilitato: 1 });
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Errore di rete';
      toast.error(`Errore salvataggio: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <div className="page-body pt-4">
        <Col xl={12} className="mb-lg-3 d-flex justify-content-end">
          <Link to="/admin/permissions/type">
            <Btn className="px-4" color="primary" type="button">
              Torna Indietro
            </Btn>
          </Link>
        </Col>

        <Col xl={12} className="mb-lg-3">
          <Card>
            <CardBody className="d-flex justify-content-center">
              <Loader />
            </CardBody>
          </Card>
        </Col>
      </div>
    );
  }

  return (
    <div className="page-body pt-4">
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody className="custom-input form-validation">
            <div className='d-flex justify-content-between w-100  mb-5'>
              <H3>
                Permessi
              </H3>
              <div>
                <Link to="/admin/tipologia">
                  <Btn className="px-4" color="primary" type="button">
                    Torna Indietro
                  </Btn>
                </Link>
              </div>
            </div>

            <Form onSubmit={handleSubmit}>
              <Row className="g-3">
                <Col xs={12}>
                  <Label htmlFor="nome">Nome</Label>
                  <Input
                    id="nome"
                    type="text"
                    placeholder="nome"
                    name="nome"
                    value={form.nome}
                    onChange={onChange}
                    required
                    disabled={loading}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="descrizione">Descrizione</Label>
                  <Input
                    id="descrizione"
                    type="textarea"
                    placeholder="descrizione"
                    name="descrizione"
                    value={form.descrizione}
                    onChange={onChange}
                    disabled={loading}
                  />
                </Col>

                <Col xs={12}>
                  <div className="card-wrapper border rounded-3 checkbox-checked">
                    <Label>Abilitato</Label>
                    <div className="radio-form">
                      <FormGroup check>
                        <Input
                          type="radio"
                          id="abilitato-si"
                          name="abilitato"
                          value="1"
                          checked={form.abilitato === 1}
                          onChange={() => setForm((prev) => ({ ...prev, abilitato: 1 }))}
                          disabled={loading}
                        />
                        <Label htmlFor="abilitato-si" check>
                          {'Sì'}
                        </Label>
                      </FormGroup>

                      <FormGroup check>
                        <Input
                          type="radio"
                          id="abilitato-no"
                          name="abilitato"
                          value="0"
                          checked={form.abilitato === 0}
                          onChange={() => setForm((prev) => ({ ...prev, abilitato: 0 }))}
                          disabled={loading}
                        />
                        <Label htmlFor="abilitato-no" check>
                          {'No'}
                        </Label>
                      </FormGroup>
                    </div>
                  </div>
                </Col>
              </Row>

              <Col xs={12} className="mt-3 d-flex align-items-center gap-2">
                <Btn className="px-4" color="primary" type="submit" disabled={loading}>
                  {isEdit ? 'Aggiorna' : 'Crea'}
                </Btn>
                {loading && <Spinner size="sm" />}
              </Col>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </div>
  );
}
